import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apps, invoices } from '../api/client';
import { InvoiceDetail } from '../../../gems/billingbyscott_client/app/javascript/billingbyscott_client/components/InvoiceDetail';

export default function InvoiceDetailPage() {
  const { appId, clientId, invoiceId } = useParams();
  const [app, setApp] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appRes, invoiceRes] = await Promise.all([
        apps.get(appId),
        invoices.get(appId, clientId, invoiceId),
      ]);
      setApp(appRes.data.app);
      setInvoice(invoiceRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [appId, clientId, invoiceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleVoid = async (invoiceId) => {
    await invoices.void(appId, clientId, invoiceId);
    await loadData();
  };

  const handleCharge = async (invoiceId) => {
    await invoices.charge(appId, clientId, invoiceId);
    await loadData();
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
      </div>
    );
  }

  // Breadcrumb header content
  const headerContent = (
    <div className="mb-4 text-sm">
      <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">Apps</Link>
      <span className="mx-2 text-gray-400">/</span>
      <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">{app?.name}</Link>
      <span className="mx-2 text-gray-400">/</span>
      <Link to={`/app/apps/${appId}/clients`} className="text-blue-600 hover:text-blue-800">Clients</Link>
      <span className="mx-2 text-gray-400">/</span>
      <Link to={`/app/apps/${appId}/clients/${clientId}`} className="text-blue-600 hover:text-blue-800">
        {invoice?.client_account?.name || `Client ${clientId}`}
      </Link>
      <span className="mx-2 text-gray-400">/</span>
      <span className="text-gray-600">Invoice {invoice?.number || `#${invoiceId}`}</span>
    </div>
  );

  // Sidebar content with subscription and client info
  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Subscription Info */}
      {invoice.subscription && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold mb-4">Subscription</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Product</dt>
              <dd>{invoice.subscription.product}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Level</dt>
              <dd>{invoice.subscription.level}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* Client Info */}
      {invoice.client_account && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold mb-4">Client</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Name</dt>
              <dd>{invoice.client_account.name || 'N/A'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">External ID</dt>
              <dd className="font-mono text-xs">{invoice.client_account.external_id}</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );

  // Construct PDF download URL
  const pdfDownloadUrl = `/api/apps/${appId}/client_accounts/${clientId}/invoices/${invoiceId}/pdf`;

  return (
    <div className="p-6">
      <InvoiceDetail
        invoice={invoice}
        showBackButton={false}
        onCharge={handleCharge}
        onVoid={handleVoid}
        onInvoicePaid={loadData}
        showLineType={true}
        headerContent={headerContent}
        sidebarContent={sidebarContent}
        pdfDownloadUrl={pdfDownloadUrl}
      />
    </div>
  );
}
